import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import CourseCard from "../../../components/CourseCard";
import { BookOpen, IndianRupee, Coins } from "lucide-react";
import PageLoader from "../../../components/PageLoader";

import {
  getCourses as apiGetCourses,
  getMyCoursePurchases,
} from "../../../api";
import { purchaseOrEnrollCourse } from "../../../utils/courseCheckout";

const normalizeCourseType = (course) =>
  course?.courseType === "crypto" ? "crypto" : "indian";
const UserBrowse = () => {
  const { user, enrollInCourse, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [enrolledSet, setEnrolledSet] = useState(new Set());
  const [courses, setCourses] = useState([]);
  const [dbPurchasedSet, setDbPurchasedSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutLoadingId, setCheckoutLoadingId] = useState(null);
  /** Browse by catalogue type from admin (default Indian) */
  const [courseTypeTab, setCourseTypeTab] = useState("indian");

  const fetchCourses = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiGetCourses();
      setCourses(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load courses",
      );
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setDbPurchasedSet(new Set());
        return;
      }
      try {
        const data = await getMyCoursePurchases();
        if (!cancelled)
          setDbPurchasedSet(
            new Set((data?.purchasedCourseIds || []).map(String)),
          );
      } catch {
        if (!cancelled) setDbPurchasedSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleEnrollNow = async (course) => {
    if (!user?.id || !course?.id) return;
    setCheckoutError("");
    setCheckoutLoadingId(course.id);
    try {
      const result = await purchaseOrEnrollCourse({
        course,
        user,
        refreshUser,
        enrollInCourse,
      });
      if (result?.dismissed) return;
      const nextId = String(course.id);
      setEnrolledSet((prev) => new Set(prev).add(nextId));
      setDbPurchasedSet((prev) => new Set(prev).add(nextId));
    } catch (e) {
      setCheckoutError(e?.message || "Could not complete enrollment");
    } finally {
      setCheckoutLoadingId(null);
    }
  };

  const visibleCourses = useMemo(() => {
    if (!courses || courses.length === 0) return [];
    return courses.filter((c) => normalizeCourseType(c) === courseTypeTab);
  }, [courses, courseTypeTab]);

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">All Courses</h1>
        <p className="text-gray-500 mt-1">
          This course is dummy for testing purpose new course will come soon
        </p>
      </div>

      <div
        className="mb-8 inline-flex rounded-full border border-gray-200/90 bg-white p-1 shadow-sm ring-1 ring-black/[0.03]"
        role="tablist"
        aria-label="Course category"
      >
        <button
          type="button"
          role="tab"
          aria-selected={courseTypeTab === "indian"}
          onClick={() => setCourseTypeTab("indian")}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
            courseTypeTab === "indian"
              ? "bg-primary-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          }`}
        >
          <IndianRupee
            className="h-4 w-4 shrink-0 opacity-95"
            strokeWidth={2}
            aria-hidden
          />
          Indian
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={courseTypeTab === "crypto"}
          onClick={() => setCourseTypeTab("crypto")}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
            courseTypeTab === "crypto"
              ? "bg-primary-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          }`}
        >
          <Coins
            className="h-4 w-4 shrink-0 opacity-95"
            strokeWidth={2}
            aria-hidden
          />
          Crypto
        </button>
      </div>

      {error && <div className="mb-4 text-sm text-danger-600">{error}</div>}
      {checkoutError && (
        <div className="mb-4 text-sm text-danger-600">{checkoutError}</div>
      )}

      {loading ? (
        <PageLoader />
      ) : visibleCourses.length === 0 ? (

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800">
            {courses.length === 0
              ? "No courses available"
              : `No ${courseTypeTab === "crypto" ? "crypto" : "Indian"} courses yet`}
          </h2>
          <p className="text-gray-500 mt-2 max-w-sm mx-auto">
            {courses.length === 0
              ? "Courses will appear here once they are added. Check back later."
              : `There are no ${courseTypeTab === "crypto" ? "crypto" : "Indian"} courses in the catalogue right now. Try the other tab.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 4xl:grid-cols-5 gap-6">
          {visibleCourses.map((course) => {
            const isEnrolled =
              dbPurchasedSet.has(String(course.id)) ||
              enrolledSet.has(String(course.id)) ||
              (user?.subscriptionFreeCourseIds || []).some(
                (id) => String(id) === String(course.id),
              );
            return (
              <CourseCard
                key={course.id}
                course={course}
                isEnrolled={isEnrolled}
                onEnrol={
                  user && !isEnrolled
                    ? () => handleEnrollNow(course)
                    : undefined
                }
                viewCoursePath={
                  user ? `/user/browse/course/${course.id}` : undefined
                }
                loginToEnrol={!user}
                enrollLoading={checkoutLoadingId === course.id}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserBrowse;
